import { useEffect, useState } from "react";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import { MapPin, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    axios.get(`${API}/events`).then(r => setEvents(r.data)).catch(() => {});
  }, []);

  // Fix timezone: date-only strings like "2026-08-01" parse as UTC midnight,
  // which shifts back a day in US timezones. Append T12:00 to prevent this.
  const parseDate = (d) => new Date(d?.includes('T') ? d : d + 'T12:00:00');

  const eventDates = events.map(e => parseDate(e.date));

  // Filter events for selected date
  const selectedEvents = selectedDate
    ? events.filter(e => parseDate(e.date).toDateString() === selectedDate.toDateString())
    : [];

  // All upcoming events sorted
  const upcomingEvents = events
    .filter(e => parseDate(e.date) >= new Date(new Date().toDateString()))
    .sort((a, b) => parseDate(a.date) - parseDate(b.date));

  return (
    <div data-testid="events-page" className="aged-paper">
      {/* Header */}
      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="segment-label">The Schedule</div>
            <div className="flex-1 h-[1px] bg-foreground/15" />
          </div>
          <h1 data-testid="events-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase mb-4">
            Events
          </h1>
          <div className="h-[3px] w-16 gold-accent" />
        </div>
      </section>

      <div className="vintage-divider" />

      {/* Calendar + Events */}
      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Calendar */}
          <div className="lg:col-span-5">
            <h2 className="font-heading text-2xl uppercase mb-6">Schedule</h2>
            <div data-testid="events-calendar" className="bg-muted p-4 sm:p-6 inline-block">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                modifiers={{ event: eventDates }}
                modifiersStyles={{
                  event: {
                    fontWeight: 'bold',
                    backgroundColor: '#97975E',
                    color: '#000000',
                  }
                }}
                className="font-body"
              />
            </div>
            {selectedDate && (
              <div data-testid="selected-date-info" className="mt-6">
                <p className="font-body text-sm text-secondary uppercase tracking-widest mb-3">
                  {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
                {selectedEvents.length > 0 ? (
                  selectedEvents.map(event => (
                    <div key={event.id} data-testid={`selected-event-${event.id}`} className="bg-foreground text-background p-6 mb-4">
                      <h3 className="font-heading text-xl uppercase mb-2">{event.title}</h3>
                      {event.venue && (
                        <p className="font-body text-sm text-background/70 flex items-center gap-2">
                          <MapPin size={14} /> {event.venue}{event.city ? `, ${event.city}` : ''}
                        </p>
                      )}
                      {event.description && (
                        <p className="font-body text-sm text-background/60 mt-3">{event.description}</p>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="font-body text-sm text-muted-foreground">No events scheduled for this date.</p>
                )}
              </div>
            )}
          </div>

          {/* Events list */}
          <div className="lg:col-span-7">
            <h2 className="font-heading text-2xl uppercase mb-6">Upcoming Cards</h2>
            {upcomingEvents.length > 0 ? (
              <div className="space-y-0">
                {upcomingEvents.map((event, idx) => (
                  <div
                    key={event.id}
                    data-testid={`upcoming-event-${event.id}`}
                    className={`py-6 ${idx > 0 ? 'border-t-2 border-foreground' : ''}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                      <div className="sm:w-32 flex-shrink-0">
                        <p className="font-heading text-lg text-primary uppercase">
                          {parseDate(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                        <p className="font-body text-xs text-secondary uppercase tracking-widest">
                          {parseDate(event.date).toLocaleDateString('en-US', { weekday: 'long' })}
                        </p>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading text-xl uppercase mb-1">{event.title}</h3>
                        {event.venue && (
                          <p className="font-body text-sm text-muted-foreground flex items-center gap-2">
                            <MapPin size={14} /> {event.venue}{event.city ? `, ${event.city}` : ''}
                          </p>
                        )}
                        {event.description && (
                          <p className="font-body text-sm text-muted-foreground mt-2">{event.description}</p>
                        )}
                        {event.is_featured && (
                          <span className="inline-block mt-2 bg-primary text-foreground font-heading text-xs uppercase px-3 py-1 tracking-wider">
                            Featured Event
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-12 text-center">
                <Clock size={32} className="mx-auto text-muted-foreground mb-4" />
                <p className="font-body text-muted-foreground">
                  No upcoming events scheduled yet. Check back soon.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
